import jobQueries from "$/lib/queries/job-queries.ts"
import storeCustomerQueries from "$/lib/queries/store-customer-queries.ts"
import storeQueries from "$/lib/queries/store-queries.ts"
import {
  CustomerClub,
  CustomerClubImportResult,
  CustomerClubMember,
  CustomerClubMembership,
  CustomerContactDetails,
  CustomerContactDetailsSearchFilters,
  JobCustomer,
  Route,
  SaveCustomerClubSettings,
  SaveCustomerContactDetails,
  populateRoute,
} from "@repo/common"
import {
  queryOptions,
  useMutation,
  useQueryClient,
} from "@tanstack/react-query"
import { request } from "../api"

interface CustomerFilters {
  storeId: string
}

async function updateCustomer(
  jobId: string,
  data: SaveCustomerContactDetails,
): Promise<JobCustomer> {
  return await request<JobCustomer>({
    url: populateRoute(Route.JOB_CUSTOMER__UPDATE, jobId),
    method: "PATCH",
    data,
  })
}

async function fetchCustomer(jobId: string): Promise<JobCustomer> {
  return await request<JobCustomer>({
    url: populateRoute(Route.JOB_CUSTOMER__FIND_ONE_BY_JOB_ID, jobId),
    method: "GET",
  })
}

export async function searchCustomerContactDetails(
  storeId: string,
  filters: CustomerContactDetailsSearchFilters,
): Promise<CustomerContactDetails[]> {
  return request({
    url: populateRoute(Route.JOB_CUSTOMER__SEARCH_CONTACT_DETAILS, storeId),
    method: "GET",
    params: {
      firstName: filters.firstName || undefined,
      lastName: filters.lastName || undefined,
      phoneNumber: filters.phoneNumber || undefined,
      email: filters.email || undefined,
    },
  })
}

async function fetchClubMembership(
  storeId: string,
  phoneNumber: string,
): Promise<CustomerClubMembership> {
  return request<CustomerClubMembership>({
    url: populateRoute(Route.JOB_CUSTOMER__FIND_CLUB_MEMBERSHIP, storeId),
    method: "GET",
    params: { phoneNumber },
  })
}

async function fetchClub(storeId: string): Promise<CustomerClub> {
  return request<CustomerClub>({
    url: populateRoute(Route.CUSTOMER_CLUB__FIND_ONE, storeId),
    method: "GET",
  })
}

async function updateClub(
  storeId: string,
  settings: SaveCustomerClubSettings,
): Promise<CustomerClub> {
  return request<CustomerClub>({
    url: populateRoute(Route.CUSTOMER_CLUB__UPDATE, storeId),
    method: "PATCH",
    data: settings,
  })
}

export function useUpdateClubMutation(storeId: string) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (settings: SaveCustomerClubSettings) =>
      updateClub(storeId, settings),
    onSuccess: () =>
      Promise.all([
        queryClient.invalidateQueries(customerQueries.club(storeId)),
        // Service packages show the club name from the store
        queryClient.invalidateQueries(storeQueries.detail(storeId)),
      ]),
  })
}

async function fetchClubMembers(
  filters: CustomerFilters,
): Promise<CustomerClubMember[]> {
  const { storeId } = filters

  return await request<CustomerClubMember[]>({
    url: populateRoute(Route.CUSTOMER_CLUB__FIND_MEMBERS, storeId),
    method: "GET",
  })
}

async function importClubMembers(
  storeId: string,
  file: File,
): Promise<CustomerClubImportResult> {
  const formData = new FormData()
  formData.append("file", file)
  return request<CustomerClubImportResult>({
    url: populateRoute(Route.CUSTOMER_CLUB__IMPORT_MEMBERS, storeId),
    method: "POST",
    data: formData,
  })
}

export function useImportClubMembersMutation(storeId: string) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (file: File) => importClubMembers(storeId, file),
    onSuccess: () =>
      Promise.all([
        queryClient.invalidateQueries({ queryKey: customerQueries.all() }),
        // The customer list shows a club badge
        queryClient.invalidateQueries({ queryKey: storeCustomerQueries.all() }),
      ]),
  })
}

export function useCustomerMutations(jobId: string, storeId: string) {
  const queryClient = useQueryClient()

  const invalidateQueries = async () => {
    await Promise.all([
      queryClient.invalidateQueries(customerQueries.list({ storeId })),
      queryClient.invalidateQueries(customerQueries.detail(jobId)),
      queryClient.invalidateQueries(jobQueries.detail(jobId)),
    ])
  }

  const updateMutation = useMutation({
    mutationFn: (customer: SaveCustomerContactDetails) =>
      updateCustomer(jobId, customer),
    onSettled: invalidateQueries,
  })

  return { updateMutation }
}

const customerQueries = {
  all: () => ["customers"],
  lists: () => [...customerQueries.all(), "list"],
  list: (filters: CustomerFilters) =>
    queryOptions({
      queryKey: [...customerQueries.lists(), filters],
      queryFn: () => fetchClubMembers(filters),
    }),
  club: (storeId: string) =>
    queryOptions({
      queryKey: [...customerQueries.all(), "club", storeId],
      queryFn: () => fetchClub(storeId),
    }),
  details: () => [...customerQueries.all(), "detail"],
  detail: (jobId: string) =>
    queryOptions({
      queryKey: [...customerQueries.details(), jobId],
      queryFn: () => fetchCustomer(jobId),
    }),
  clubMembership: (storeId: string, phoneNumber: string) =>
    queryOptions({
      queryKey: [
        ...customerQueries.all(),
        "club-membership",
        storeId,
        phoneNumber,
      ],
      queryFn: () => fetchClubMembership(storeId, phoneNumber),
    }),
}

export default customerQueries
